import { Storage } from 'app/shared/util/storage';

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios, { AxiosResponse } from 'axios';

import { AppThunk } from 'app/config/store';
import { AUTHENTICATION_TOKEN_KEY, Authority } from 'app/shared/api/constants';

import { serializeAxiosError } from './reducer.utils';

export const initialState = {
  loading: false,
  isAuthenticated: false,
  loginSuccess: false,
  loginError: false, // Errors returned from server side
  loginForbidden: false, // 403: authenticated OK but the account is not an admin
  showModalLogin: false,
  account: {} as any,
  errorMessage: null as unknown as string, // Errors returned from server side
  redirectMessage: null as unknown as string,
  sessionHasBeenFetched: false,
  logoutUrl: null as unknown as string,
};

export type AuthenticationState = Readonly<typeof initialState>;

// Actions

export const getSession = (): AppThunk => async (dispatch, getState) => {
  await dispatch(getAccount());

  const { account } = getState().authentication;

  // The backoffice is ADMIN-only. If a non-admin authenticates (e.g. a customer token pasted into
  // localStorage), drop the session and show the "solo administradores" login instead of a half-rendered,
  // unauthorized shell with an account menu. The backend already 403s every /api/admin/** call — this just
  // cleans up the client state so a non-admin never sits inside the backoffice.
  if (account?.activated && !account?.authorities?.includes(Authority.ADMIN)) {
    clearAuthToken();
    dispatch(adminOnlyRejected());
    return;
  }
};

export const getAccount = createAsyncThunk('authentication/get_account', async () => axios.get<any>('api/account'), {
  serializeError: serializeAxiosError,
});

interface IAuthParams {
  username: string;
  password: string;
  rememberMe?: boolean;
}

export const authenticate = createAsyncThunk(
  'authentication/login',
  // Backoffice uses the admin-only login endpoint (rejects non-admins with 403); the shared
  // /api/authenticate is reserved for the customer website and the business app.
  async (auth: IAuthParams) => axios.post<any>('api/admin/authenticate', auth),
  {
    serializeError: serializeAxiosError,
  },
);

export const login: (username: string, password: string, rememberMe?: boolean) => AppThunk =
  (username, password, rememberMe = false) =>
  async dispatch => {
    const result = await dispatch(authenticate({ username, password, rememberMe }));
    const response = result.payload as AxiosResponse;
    const bearerToken = response?.headers?.authorization;
    if (bearerToken?.startsWith('Bearer ')) {
      const jwt = bearerToken.slice(7, bearerToken.length);
      if (rememberMe) {
        Storage.local.set(AUTHENTICATION_TOKEN_KEY, jwt);
      } else {
        Storage.session.set(AUTHENTICATION_TOKEN_KEY, jwt);
      }
    }
    dispatch(getSession());
  };

export const clearAuthToken = () => {
  if (Storage.local.get(AUTHENTICATION_TOKEN_KEY)) {
    Storage.local.remove(AUTHENTICATION_TOKEN_KEY);
  }
  if (Storage.session.get(AUTHENTICATION_TOKEN_KEY)) {
    Storage.session.remove(AUTHENTICATION_TOKEN_KEY);
  }
};

export const logout: () => AppThunk = () => dispatch => {
  clearAuthToken();
  dispatch(logoutSession());
};

export const clearAuthentication = messageKey => dispatch => {
  clearAuthToken();
  dispatch(authError(messageKey));
  dispatch(clearAuth());
};

export const AuthenticationSlice = createSlice({
  name: 'authentication',
  initialState: initialState as AuthenticationState,
  reducers: {
    logoutSession() {
      return {
        ...initialState,
        showModalLogin: true,
      };
    },
    authError(state, action) {
      return {
        ...state,
        showModalLogin: true,
        redirectMessage: action.payload,
      };
    },
    clearAuth(state) {
      return {
        ...state,
        loading: false,
        showModalLogin: true,
        isAuthenticated: false,
      };
    },
    adminOnlyRejected() {
      // Non-admin authenticated in the ADMIN-only backoffice: reset session and show the admin-only login.
      return {
        ...initialState,
        sessionHasBeenFetched: true,
        showModalLogin: true,
        loginForbidden: true,
      };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(authenticate.rejected, (state, action) => ({
        ...initialState,
        errorMessage: action.error.message!,
        showModalLogin: true,
        loginError: true,
        // serializeAxiosError keeps the raw AxiosError, so the HTTP status is available here.
        // 403 = valid credentials but not an admin (see AdminAuthenticateController).
        loginForbidden: (action.error as any)?.response?.status === 403,
      }))
      .addCase(authenticate.fulfilled, state => ({
        ...state,
        loading: false,
        loginError: false,
        showModalLogin: false,
        loginSuccess: true,
      }))
      .addCase(getAccount.rejected, (state, action) => ({
        ...state,
        loading: false,
        isAuthenticated: false,
        sessionHasBeenFetched: true,
        showModalLogin: true,
        errorMessage: action.error.message!,
      }))
      .addCase(getAccount.fulfilled, (state, action) => {
        const isAuthenticated = action.payload?.data?.activated;
        return {
          ...state,
          isAuthenticated,
          loading: false,
          sessionHasBeenFetched: true,
          account: action.payload.data,
        };
      })
      .addCase(authenticate.pending, state => {
        state.loading = true;
      })
      .addCase(getAccount.pending, state => {
        state.loading = true;
      });
  },
});

export const { logoutSession, authError, clearAuth, adminOnlyRejected } = AuthenticationSlice.actions;

// Reducer
export default AuthenticationSlice.reducer;
